import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';

import { type ButtonProps, Button } from '@playbooks/ui/components/button';
import { type StatusTone, statusBadgeToneVariants } from '@playbooks/ui/components/component-recipes';
import { Icon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';

import { type VariantProps, cva } from 'class-variance-authority';

const badgeVariants = cva(
	'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-focus focus-visible:ring-[3px] focus-visible:ring-focus/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-danger aria-invalid:ring-danger/20 dark:aria-invalid:ring-danger/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
	{
		variants: {
			size: {
				default: '',
				xs: '',
				sm: 'h-6 px-2.5',
				lg: 'h-7 px-3 text-sm',
			},
			variant: {
				default: 'border-border bg-surface text-foreground [a]:hover:bg-surface-hover',
				secondary: 'bg-surface-hover text-foreground [a]:hover:bg-surface-selected',
				destructive:
					'bg-danger/10 text-danger focus-visible:ring-danger/20 dark:bg-danger/20 dark:focus-visible:ring-danger/40 [a]:hover:bg-danger/20',
				outline: 'border-border text-foreground [a]:hover:bg-surface-hover [a]:hover:text-foreground-body',
				ghost: 'hover:bg-surface-hover hover:text-foreground-body dark:hover:bg-surface-hover/50',
				link: 'text-foreground underline-offset-4 hover:underline',
				draft: 'bg-surface-hover text-foreground-body',
				info: 'bg-surface-hover text-foreground-nav',
				modified: 'bg-warning/10 text-warning',
				pending: 'bg-warning/10 text-warning',
				warning: 'bg-warning/10 text-warning',
				paused: 'bg-warning/10 text-warning',
				active: 'border-success/30 bg-success/10 text-success',
				ready: 'border-success/30 bg-success/10 text-success',
				complete: 'bg-success/10 text-success',
				succeeded: 'bg-success/10 text-success',
				verified: 'bg-success/10 text-success',
				finished: 'bg-surface-hover text-foreground-body',
				ended: 'bg-surface-hover text-foreground-body',
				inactive: 'bg-surface-hover text-foreground-body',
				closed: 'bg-surface-hover text-foreground-body',
				stopped: 'bg-surface-hover text-foreground-body',
				paid: 'bg-surface-hover text-foreground-body',
				won: 'bg-surface-hover text-foreground-body',
				canceled: 'bg-danger/10 text-danger',
				denied: 'bg-danger/10 text-danger',
				disabled: 'bg-danger/10 text-danger',
				errored: 'bg-danger/10 text-danger',
				refunded: 'bg-danger/10 text-danger',
			},
		},
		defaultVariants: {
			size: 'default',
			variant: 'default',
		},
	},
);

type BadgeProps = useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { tone?: StatusTone };

function Badge({ className, size = 'default', variant = 'default', tone, render, ...props }: BadgeProps) {
	return useRender({
		defaultTagName: 'span',
		props: mergeProps<'span'>(
			{
				className: cn(badgeVariants({ size, variant }), tone && statusBadgeToneVariants({ tone }), className),
			},
			props,
		),
		render,
		state: {
			slot: 'badge',
			size,
			tone,
			variant,
		},
	});
}

function BadgeMedia({ className, ...props }: React.ComponentProps<'span'>) {
	// Theme
	const rootClassName = cn(
		'flex size-3.5 shrink-0 items-center justify-center overflow-hidden rounded-full [&_img]:size-full [&_img]:object-cover',
		className,
	);

	// Render
	return <span data-slot='badge-media' className={rootClassName} {...props} />;
}

function BadgeRemoveButton({ className, 'aria-label': ariaLabel = 'Remove', ...props }: ButtonProps) {
	// Theme
	const rootClassName = cn('-mr-1 size-5 p-0', className);

	// Render
	return (
		<Button variant='ghost' size='icon-xs' aria-label={ariaLabel} {...props} className={rootClassName}>
			<Icon type='far' icon='xmark' aria-hidden />
		</Button>
	);
}

export { Badge, BadgeMedia, BadgeRemoveButton, badgeVariants, type BadgeProps };
